import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { actionClassName, itemKey } from "./MegaMenuBlock.shared";
import { MegaMenuDesktopNav } from "./MegaMenuDesktopNav.client";
import { MegaMenuMobileDrawer } from "./MegaMenuMobileDrawer.client";
import type { IMegaMenuBlockProps, MegaMenuAction } from "./MegaMenuBlock.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const ActionList = ({
  actions,
  compact = false,
  className,
}: {
  actions?: MegaMenuAction[] | null;
  compact?: boolean;
  className?: string;
}) => {
  const visibleActions = actions ?? [];

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap gap-3", compact && "flex-col sm:flex-row", className)}>
      {visibleActions.map((action, index) => (
        <Link
          key={itemKey("action", index, action)}
          aria-label={action.ariaLabel}
          className={actionClassName(action.style)}
          href={action.href}
          rel={action.target === "_blank" ? "noopener noreferrer" : undefined}
          target={action.target ?? undefined}
        >
          {action.text}
        </Link>
      ))}
    </div>
  );
};

const LogoMark = ({ logo }: { logo?: IMegaMenuBlockProps["logo"] }) => {
  if (!logo?.defaultSrc) {
    return null;
  }

  return (
    <Link
      aria-label={logo.alternateText || "Go to home page"}
      className="group/logo inline-flex rounded-2xl p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2"
      href="/"
    >
      <span className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#D9E1FF] bg-[#F8FAFF] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] transition-colors duration-200 group-hover/logo:border-[#8FA3FF] motion-reduce:transition-none lg:size-12">
        <Image
          alt={logo.alternateText}
          className="h-full w-full"
          height={logo.height ?? undefined}
          imageClassName="h-full w-full object-contain"
          responsiveProfile="compact"
          sizes="(min-width: 64rem) 3.5rem, 3rem"
          src={logo.defaultSrc}
          width={logo.width ?? undefined}
        />
      </span>
    </Link>
  );
};

export const MegaMenuBlock = (props: IMegaMenuBlockProps) => {
  const items = props.items ?? [];
  const hasActions = Boolean(props.actions?.length);

  if (!props.logo && items.length === 0 && !hasActions) {
    return null;
  }

  return (
    <header className={cn("relative z-40 bg-background text-[#151927]", props.parentClass)}>
      <nav aria-label="Primary navigation" className="kaaj-container py-2.5">
        <div className="rounded-[28px] border border-[#D9E1FF] bg-white/92 px-3 py-2 shadow-[0_18px_60px_rgba(33,68,255,0.10)] backdrop-blur-xl lg:px-4">
          <div className="flex items-center justify-between gap-3 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-4">
            <div className="flex min-w-0 items-center justify-start">
              <LogoMark logo={props.logo} />
            </div>

            <MegaMenuDesktopNav items={items} />

            <div className="hidden min-w-0 items-center justify-end lg:flex">
              <ActionList actions={props.actions} className="flex-nowrap whitespace-nowrap" />
            </div>

            {items.length || hasActions ? (
              <div className="ml-auto lg:hidden">
                <MegaMenuMobileDrawer actions={props.actions} items={items} label="Menu" />
              </div>
            ) : null}
          </div>
        </div>
      </nav>
    </header>
  );
};

MegaMenuBlock.displayName = "Block::MegaMenuBlock";
